import * as React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import type { FileRouteTypes } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { useIsTerminalScreen } from '../../../../hooks/useIsTerminalScreen';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import TerminalScreenMessage from '../../../../components/navigation/TerminalScreenMessage';
import { Heading } from '../../../../components/Heading';
import { classNames } from '../../../../style/classNames';
import type { Chart, PatientDoc } from '@usacsmideo/types';
import * as Sentry from '@sentry/browser';
import { toast } from 'sonner';

import { getAppleWalletPass } from '../../../../data/wallets/getAppleWalletPass';
import { getGoogleWalletPass } from '../../../../data/wallets/getGoogleWalletPass';
import { sendAutomatedEmail } from '../../../../data/sendAutomatedEmail';
import { base64ToBlob } from '../../../../utilities/base64ToBlob';
import { triggerBlobDownload } from '../../../../utilities/triggerBlobDownload';
import { detectPlatform } from '../../../../utilities/detectPlatform';

export const Route = createFileRoute('/_authenticated/$patientId/card/addToWallet')({
  component: AddToWalletPage,
});

function AddToWalletPage() {
  const { currentUser } = useAuth();
  const patientId = currentUser?.uid || '';
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const isTerminal = useIsTerminalScreen(pathname, patientInfo?.navObj);

  const [appleLoading, setAppleLoading] = React.useState(false);
  const [googleLoading, setGoogleLoading] = React.useState(false);
  const [googleSaveUrl, setGoogleSaveUrl] = React.useState<string | null>(null);
  const [emailLoading, setEmailLoading] = React.useState(false);
  const [emailAddress, setEmailAddress] = React.useState(
    patientInfo?.contactEmail || patientInfo?.accountEmail || '',
  );
  const [emailSent, setEmailSent] = React.useState(false);

  const platform = React.useMemo(() => detectPlatform(), []);
  const isMobile = platform === 'ios' || platform === 'android';

  const handleAppleDownload = async () => {
    setAppleLoading(true);
    try {
      const response = await getAppleWalletPass(patientId);
      const blob = base64ToBlob(response.fileContent, response.contentType);
      triggerBlobDownload(blob, response.fileName);
      toast.success('Apple Wallet pass downloaded successfully.');
    } catch {
      toast.error('Failed to download Apple Wallet pass. Please try again.');
    } finally {
      setAppleLoading(false);
    }
  };

  const handleGoogleWallet = async () => {
    setGoogleLoading(true);
    setGoogleSaveUrl(null);
    try {
      const response = await getGoogleWalletPass(patientId);
      setGoogleSaveUrl(response.saveUrl);
      toast.success('Your Google Wallet pass is ready! Tap the link below to add it.');
    } catch {
      toast.error('Failed to get Google Wallet pass. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleEmailPass = async () => {
    if (!emailAddress || !/.+@.+\..+/.test(emailAddress)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setEmailLoading(true);
    try {
      // Generate .pkpass first (ensures it exists in Storage with latest data)
      await getAppleWalletPass(patientId);
      // Send the email with the .pkpass attached
      await sendAutomatedEmail({
        type: 'walletPassEmail',
        patientId,
        email: emailAddress,
        name: `${chart?.PATIENT_INFO_1?.firstName} ${chart?.PATIENT_INFO_1?.lastName}`,
      });
      setEmailSent(true);
      toast.success('Wallet pass email sent successfully.');
    } catch {
      toast.error('Failed to send wallet pass email. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo,
      chart,
    });

    if ('incompleteMessage' in res) {
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    if ('complete' in res) {
      mutate(
        { patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/$patientId', params: { patientId } });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }

    mutate(
      { patientId, navObj: res.updatedNavObj },
      {
        onSuccess: () => {
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  if (!patientInfo || !patientInfo.navObj) {
    return <p>Loading...</p>;
  }

  // Order sections based on platform: mobile shows download first, desktop shows email first
  const downloadSection = (
    <div className="mt-6 w-full max-w-md">
      <h2 className="text-xl font-bold">Download Directly</h2>
      <p className="mt-2 text-lg">Save your MIDEO card to your device&apos;s digital wallet.</p>
      <div className="mt-4 flex flex-col gap-3">
        <button
          onClick={handleAppleDownload}
          disabled={appleLoading}
          className="rounded-lg bg-black px-6 py-3 text-lg font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {appleLoading ? 'Downloading...' : 'Add to Apple Wallet'}
        </button>
        {googleSaveUrl ? (
          <a
            href={googleSaveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-lg bg-blue-600 px-6 py-3 text-center text-lg font-semibold text-white hover:bg-blue-700"
          >
            Open Google Wallet Pass
          </a>
        ) : (
          <button
            onClick={handleGoogleWallet}
            disabled={googleLoading}
            className="rounded-lg bg-blue-600 px-6 py-3 text-lg font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {googleLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Preparing...
              </span>
            ) : (
              'Add to Google Wallet'
            )}
          </button>
        )}
      </div>
    </div>
  );

  const emailSection = (
    <div className="mt-6 w-full max-w-md">
      <h2 className="text-xl font-bold">Email Wallet Pass</h2>
      <p className="mt-2 text-lg">
        Send your Apple Wallet pass to any email address. Open the attached file on an iPhone to add it to Apple Wallet.
      </p>
      <div className="mt-4 flex flex-col gap-3">
        <input
          type="email"
          value={emailAddress}
          onChange={(e) => {
            setEmailAddress(e.target.value);
            setEmailSent(false);
          }}
          placeholder="Enter email address"
          className="rounded-lg border border-gray-300 px-4 py-3 text-lg focus:border-blue-500 focus:outline-none"
        />
        <button
          onClick={handleEmailPass}
          disabled={emailLoading || emailSent}
          className="rounded-lg bg-green-600 px-6 py-3 text-lg font-semibold text-white hover:bg-green-700 disabled:opacity-50"
        >
          {emailLoading ? 'Sending...' : emailSent ? 'Email Sent' : 'Send Wallet Pass'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex w-full flex-col items-center">
      <Heading id="add-to-wallet" textContent="Add to Digital Wallet" />
      <p className="max-w-lg text-center text-xl">
        Add your MIDEO card to your phone&apos;s digital wallet for quick access in an emergency.
      </p>
      {isMobile ? (
        <>
          {downloadSection}
          {emailSection}
        </>
      ) : (
        <>
          {emailSection}
          {downloadSection}
        </>
      )}
      {isTerminal ? (
        <TerminalScreenMessage onReturnToOverview={handleNextButtonClick} />
      ) : (
        <button onClick={handleNextButtonClick} className={classNames.nextButton}>
          Next
        </button>
      )}
    </div>
  );
}
